import { ToastMessageInterface } from "@app/interfaces/ToastMessageInterface";
import { themeAtom } from "@app/models/AppStates";
import { RoutesEnum } from "@app/models/RoutesEnum";
import { ThemeEnum } from "@app/models/ThemeEnum";
import { TokenUsageService } from "@app/services/TokenUsageService";
import { UserService } from "@app/services/UserService";
import { ModelCardService } from "@app/services/ModelCardService";
import {
    buildModelCardLookup,
    calculateTokenCost,
    formatCost,
    resolveModelCard,
    summarizeTokenUsage
} from "@app/utils/TokenUsagePricingUtil";
import { ModelCardInterface, TokenUsageInterface } from "@dashart/dashart-gpt-shared-library/dist/index";
import UserInterface from "@dashart/dashart-gpt-shared-library/dist/interfaces/UserInterface";
import ButtonWithLink from "@view/components/buttonWithLink/ButtonWithLink";
import ProgressSpinnerOverlay from "@view/components/progressSpinnerOverlay/ProgressSpinnerOverlay";
import RoleValidatorComponent from "@view/components/roleValidatorComponent/RoleValidatorComponent";
import { DateTime } from "luxon";
// Import Chart.js first
import 'chart.js/auto';
import { Calendar } from "primereact/calendar";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { useDebounce } from "primereact/hooks";
import { Row } from "primereact/row";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import "./UserTokenUsageScreen.scss";

export interface UserTokenUsageScreenProps {
    currentUser: UserInterface;
    showToast: (toast: ToastMessageInterface) => void
}

interface TimePeriodOption {
    label: string;
    value: 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month';
}

const UserTokenUsageScreen: React.FunctionComponent<UserTokenUsageScreenProps> = (props: UserTokenUsageScreenProps) => {
    const { currentUser } = props;
    const { t } = useTranslation();
    const { userId } = useParams();
    const currentTheme = useAtomValue(themeAtom);

    const [userData, setUserData] = useState<UserInterface | null>(null);
    const [tokenUsageData, setTokenUsageData] = useState<TokenUsageInterface[]>([]);
    const [modelCards, setModelCards] = useState<Record<string, ModelCardInterface>>({});
    const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriodOption | null>(null);
    const [chartMode, setChartMode] = useState<'tokens' | 'costs'>('tokens');
    const [chartData, setChartData] = useState(null);
    const [chartOptions, setChartOptions] = useState(null);

    const [dateRange, dateRangeDebounced, setDateRange] = useDebounce([
        DateTime.now().minus({ days: 30 }).toJSDate(),
        DateTime.now().toJSDate(),
    ], 1000);
    const [serverActionPending, setServerActionPending] = useState(false);

    // Time period options for the dropdown
    const timePeriodOptions: TimePeriodOption[] = [
        { label: t("user_management.usage_screen.time_periods.today"), value: 'today' },
        { label: t("user_management.usage_screen.time_periods.yesterday"), value: 'yesterday' },
        { label: t("user_management.usage_screen.time_periods.this_week"), value: 'this_week' },
        { label: t("user_management.usage_screen.time_periods.last_week"), value: 'last_week' },
        { label: t("user_management.usage_screen.time_periods.this_month"), value: 'this_month' },
        { label: t("user_management.usage_screen.time_periods.last_month"), value: 'last_month' },
    ];

    const navigate = useNavigate();

    /**
     * Load user from backend
     * @param userId
     */
    const loadUser = async (userId: string) => {
        setServerActionPending(true);
        try {
            setUserData(await UserService.getOne(userId));
        } catch (error) {
            props.showToast({
                severity: "error",
                summary: t('globals.loadFailedNotice'),
                detail: JSON.stringify(error ?? '')
            });
            navigate(RoutesEnum.userList);
        }
        setServerActionPending(false);
    };

    /**
     * Load user from backend
     * @param userId
     */
    const loadUserTokenUsage = async (userId: string | undefined | null) => {

        if (!userId) {
            return;
        }

        setServerActionPending(true);

        const startTimeStamp = dateRange[0];
        const endTimeStamp = dateRange[1] ?? new Date();

        // start date should be start of day
        startTimeStamp.setHours(0, 0, 0, 0);
        // end date should be end of day
        endTimeStamp.setHours(23, 59, 59, 0);


        try {
            setTokenUsageData(await TokenUsageService.getUserTokenUsageByTimeRange(
                {
                    userId: +userId,
                    startTimeStamp: startTimeStamp.getTime(),
                    endTimeStamp: endTimeStamp.getTime(),
                }
            ));
        } catch (error) {
            props.showToast({
                severity: "error",
                summary: t('globals.loadFailedNotice'),
                detail: JSON.stringify(error ?? '')
            });
            navigate(RoutesEnum.userList);
        }
        setServerActionPending(false);
    };

    useEffect(() => {
        if (userId) {
            loadUser(userId);
        }
    }, [userId]);

    useEffect(() => {
        if (userData) {
            loadUserTokenUsage(
                userId
            );
        }
    }, [userData]);

    useEffect(() => {
        if (userData) {
            loadUserTokenUsage(
                userId
            );
        }
    }, [dateRangeDebounced]);

    /**
     * Load model cards so per-token pricing can be looked up for each usage entry.
     * Pricing is best-effort: the view still works when cards are unavailable.
     */
    useEffect(() => {
        const loadModelCards = async () => {
            try {
                const grouped = await ModelCardService.getInstance().getAllModels();
                setModelCards(buildModelCardLookup(grouped));
            } catch (error) {
                // ignore - pricing is optional
            }
        };
        loadModelCards();
    }, []);

    // Prepare chart data when token usage data or date range changes
    useEffect(() => {
        prepareChartData();
    }, [tokenUsageData, dateRange, t, currentTheme, modelCards, chartMode]);

    const onDateRangeChange = (e: any) => {
        setDateRange(e.value as [Date, Date]);
        setSelectedTimePeriod(null); // Reset selected time period when manually changing date range
    }

    // Set date range based on selected time period
    const setTimePeriod = (period: 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month') => {
        const now = DateTime.now();
        let startDate: DateTime;
        let endDate: DateTime = now;

        switch (period) {
            case 'today':
                startDate = now.startOf('day');
                break;
            case 'yesterday':
                startDate = now.minus({ days: 1 }).startOf('day');
                endDate = now.minus({ days: 1 }).endOf('day');
                break;
            case 'this_week':
                startDate = now.startOf('week');
                break;
            case 'last_week':
                startDate = now.minus({ weeks: 1 }).startOf('week');
                endDate = now.minus({ weeks: 1 }).endOf('week');
                break;
            case 'this_month':
                startDate = now.startOf('month');
                break;
            case 'last_month':
                startDate = now.minus({ months: 1 }).startOf('month');
                endDate = now.minus({ months: 1 }).endOf('month');
                break;
            default:
                startDate = now.minus({ days: 30 });
        }

        setDateRange([startDate.toJSDate(), endDate.toJSDate()]);
    }

    // Handle time period dropdown change
    const onTimePeriodChange = (option: DropdownChangeEvent) => {
        setSelectedTimePeriod(option.value);
        if (option) {
            setTimePeriod(option.value);
        }
    }

    const tokenUsageSummary = summarizeTokenUsage(tokenUsageData, modelCards);

    // Get theme-aware colors for chart
    const getChartColors = () => {
        const isDarkTheme = currentTheme === ThemeEnum.DARK;
        
        if (isDarkTheme) {
            return {
                total: { bg: '#60A5FA', border: '#3B82F6' },    // blue-400/500
                input: { bg: '#34D399', border: '#10B981' },    // emerald-400/500  
                output: { bg: '#FBBF24', border: '#F59E0B' }    // amber-400/500
            };
        } else {
            return {
                total: { bg: '#42A5F5', border: '#1976D2' },    // blue
                input: { bg: '#66BB6A', border: '#388E3C' },    // green
                output: { bg: '#FFA726', border: '#F57C00' }    // orange
            };
        }
    };

    // Group token usage data by date or time intervals
    const prepareChartData = () => {
        // Determine if we should group by hour or by day based on the date range
        const start = dateRange[0];
        const end = dateRange[1] || new Date();
        const daysInRange = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        const groupByHour = daysInRange <= 2; // Group by hour if range is 2 days or less

        // Generate all time intervals within the range
        const timeIntervals = [];
        const startDt = DateTime.fromJSDate(start);
        const endDt = DateTime.fromJSDate(end);

        if (groupByHour) {
            let currentHour = startDt.startOf('hour');
            while (currentHour <= endDt) {
                timeIntervals.push(currentHour.toFormat('yyyy-MM-dd HH:00'));
                currentHour = currentHour.plus({ hours: 1 });
            }
        } else {
            let currentDay = startDt.startOf('day');
            while (currentDay <= endDt) {
                timeIntervals.push(currentDay.toFormat('yyyy-MM-dd'));
                currentDay = currentDay.plus({ days: 1 });
            }
        }

        // Initialize data structures with all time intervals and zero counts
        const groupedData: Record<string, number> = {};
        const inputData: Record<string, number> = {};
        const outputData: Record<string, number> = {};

        timeIntervals.forEach(timeKey => {
            groupedData[timeKey] = 0;
            inputData[timeKey] = 0;
            outputData[timeKey] = 0;
        });

        // Process token usage data if available
        if (tokenUsageData && tokenUsageData.length > 0) {
            // Sort the data by createdAt date
            const sortedData = [...tokenUsageData].sort((a, b) => {
                return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            });

            sortedData.forEach((entry) => {
                const date = DateTime.fromISO(entry.createdAt);
                let timeKey;

                if (groupByHour) {
                    timeKey = date.toFormat('yyyy-MM-dd HH:00');
                } else {
                    timeKey = date.toFormat('yyyy-MM-dd');
                }

                // Only add if the timeKey is within our range
                if (groupedData[timeKey] !== undefined) {
                    // chartMode 'costs' plots the monetary value instead of token count
                    const value = chartMode === 'tokens'
                        ? entry.tokenCount
                        : (calculateTokenCost(entry, resolveModelCard(modelCards, entry.modelId)) ?? 0);
                    groupedData[timeKey] += value;

                    if (entry.tokenType === 'input') {
                        inputData[timeKey] += value;
                    } else {
                        outputData[timeKey] += value;
                    }
                }
            });
        }

        // Prepare the labels (x-axis) and data (y-axis)
        const labels = timeIntervals.map(timeKey => {
            if (groupByHour) {
                return DateTime.fromFormat(timeKey, 'yyyy-MM-dd HH:00').toFormat('dd.MM HH:00');
            } else {
                return DateTime.fromFormat(timeKey, 'yyyy-MM-dd').toFormat('dd.MM');
            }
        });

        // Get the values from our maps in the same order as the labels
        const totalValues = timeIntervals.map(key => groupedData[key]);
        const inputValues = timeIntervals.map(key => inputData[key]);
        const outputValues = timeIntervals.map(key => outputData[key]);

        // Get theme-appropriate colors
        const colors = getChartColors();

        // Set chart data
        setChartData({
            labels: labels,
            datasets: [
                {
                    label: t(chartMode === 'tokens'
                        ? 'user_management.usage_screen.chart.total'
                        : 'user_management.usage_screen.chart.total_cost'),
                    backgroundColor: colors.total.bg,
                    borderColor: colors.total.border,
                    borderWidth: 1,
                    data: totalValues
                },
                {
                    label: t(chartMode === 'tokens'
                        ? 'user_management.usage_screen.chart.input'
                        : 'user_management.usage_screen.chart.input_cost'),
                    backgroundColor: colors.input.bg,
                    borderColor: colors.input.border,
                    borderWidth: 1,
                    data: inputValues
                },
                {
                    label: t(chartMode === 'tokens'
                        ? 'user_management.usage_screen.chart.output'
                        : 'user_management.usage_screen.chart.output_cost'),
                    backgroundColor: colors.output.bg,
                    borderColor: colors.output.border,
                    borderWidth: 1,
                    data: outputValues
                }
            ]
        });

        // Set chart options with improved handling for many data points
        const isLargeDataset = labels.length > 20;
        const isDarkTheme = currentTheme === ThemeEnum.DARK;
        
        const textColor = isDarkTheme ? '#e5e7eb' : '#6b7280'; // gray-200 : gray-500
        const gridColor = isDarkTheme ? '#4b5563' : '#6b7280'; // gray-600 : gray-500

        setChartOptions({
            maintainAspectRatio: false,
            aspectRatio: 1.2,
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        color: textColor,
                        font: {
                            size: 12
                        },
                        padding: 20
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: isDarkTheme ? '#111827' : '#ffffff',
                    titleColor: isDarkTheme ? '#f9fafb' : '#111827',
                    bodyColor: isDarkTheme ? '#e5e7eb' : '#374151',
                    borderColor: isDarkTheme ? '#4b5563' : '#d1d5db',
                    borderWidth: 1,
                }
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: groupByHour
                            ? t('user_management.usage_screen.chart.hour')
                            : t('user_management.usage_screen.chart.date'),
                        color: textColor,
                        font: {
                            size: 12,
                            weight: 'bold'
                        }
                    },
                    ticks: {
                        maxRotation: 45,
                        minRotation: 45,
                        color: textColor,
                        font: {
                            size: 11
                        },
                        // Show fewer ticks when there are many data points
                        autoSkip: isLargeDataset,
                        maxTicksLimit: isLargeDataset ? 15 : undefined
                    },
                    grid: {
                        color: gridColor,
                        drawBorder: true
                    }
                },
                y: {
                    title: {
                        display: true,
                        text: t(chartMode === 'tokens'
                            ? 'user_management.usage_screen.chart.token_count'
                            : 'user_management.usage_screen.chart.cost'),
                        color: textColor,
                        font: {
                            size: 12,
                            weight: 'bold'
                        }
                    },
                    ticks: {
                        color: textColor,
                        font: {
                            size: 11
                        }
                    },
                    grid: {
                        color: gridColor,
                        drawBorder: true
                    },
                    beginAtZero: true
                }
            }
        });
    };

    const timePeriodDropdown = (
        <div className="time-period-dropdown flex mb-2">
            <Dropdown
                value={selectedTimePeriod}
                options={timePeriodOptions}
                onChange={(e) => onTimePeriodChange(e)}
                optionLabel="label"
                placeholder={t("user_management.usage_screen.select_time_period")}
                className="w-full md:w-14rem"
            />
        </div>
    );

    const chartModeSwitch = (
        <div className="chart-mode-switch">
            <SelectButton
                value={chartMode}
                onChange={(e: any) => setChartMode(e.value)}
                options={[
                    { label: t('user_management.usage_screen.chart.unit_tokens'), value: 'tokens' },
                    { label: t('user_management.usage_screen.chart.unit_costs'), value: 'costs' },
                ]}
            />
        </div>
    );

    const tableHeader = (
        <>
            <div className={'flex grid justify-content-end align-items-center'}>
                <div className={'mr-2 flex-grow-1'}>
                    {t("user_management.usage_screen.header", userData ?? {})}
                </div>
                <div className={'mr-2'}>
                    {t("user_management.usage_screen.date_range")}:
                </div>
                <Calendar
                    value={dateRange}
                    selectionMode="range" readOnlyInput
                    showButtonBar={true}
                    onChange={onDateRangeChange}
                />
                {timePeriodDropdown}
            </div>
            <div className="chart-container relative">
                {chartData && chartOptions ? (
                    <Chart
                        type="bar"
                        data={chartData}
                        options={chartOptions}
                        style={{ height: '200px', width: '100%' }}
                    />
                ) : (
                    <div className="no-data-message p-3 text-center">
                        {t("user_management.usage_screen.chart.no_data")}
                    </div>
                )}
                <div className="chart-mode-row absolute top-0 right-0 z-10 mt-2 mr-2">
                    {chartModeSwitch}
                </div>
            </div>
        </>
    );

    const tableSummaryFooter = (
        <ColumnGroup>
            <Row>
                <Column footer={`Total (${tokenUsageData?.length ?? 0})`} colSpan={3} />
                <Column footer={
                    t(
                        "user_management.usage_screen.table.summary_tokens",
                        {...tokenUsageSummary}
                    )
                } />
                <Column footer="" colSpan={2} />
            </Row>
            <Row>
                <Column footer={t("user_management.usage_screen.table.header_cost")} colSpan={3} />
                <Column footer="" />
                <Column footer={
                    t(
                        "user_management.usage_screen.table.summary_cost",
                        {
                            cost: formatCost(tokenUsageSummary.totalCost),
                            inputCost: formatCost(tokenUsageSummary.inputCost),
                            outputCost: formatCost(tokenUsageSummary.outputCost),
                            reasoningCost: formatCost(tokenUsageSummary.reasoningCost),
                        }
                    )
                } />
                <Column footer="" />
            </Row>
            <Row>
                <Column footer={
                    <div className={'flex grid justify-content-end align-items-center pt-1'}>
                        <ButtonWithLink
                            key={`btn-back`}
                            data-test-id={"btn-back"}
                            className="button-secondary"
                            label={t('globals.actions.backToOverview')}
                            icon="pi pi-chevron-left"
                            href={RoutesEnum.userList}
                        />
                    </div>
                } colSpan={6} />
            </Row>
        </ColumnGroup>
    );

    return <div className='UserTokenUsageScreen'>
        {/*Validate access rights*/}
        <RoleValidatorComponent
            currentUser={currentUser}
            validationType={'userScope'}
            userOfInterest={userData ?? null}
            redirectRouteIfNotAllowed={RoutesEnum.dashboard}
        />

        <ProgressSpinnerOverlay visible={serverActionPending} />

        <DataTable
            value={tokenUsageData}
            className={'m-2'}
            scrollable scrollHeight={'flex'}
            rowHover={true}
            header={tableHeader}
            footerColumnGroup={tableSummaryFooter as any}
        >
            <Column
                field="id"
                header={t("user_management.usage_screen.table.header_id")}
                sortable={true}
            />
            <Column
                field="modelId"
                header={t("user_management.usage_screen.table.header_model")}
                sortable={true} />
            <Column
                field="tokenType"
                header={t("user_management.usage_screen.table.header_tokenType")}
                sortable={true} />
            <Column
                field="tokenCount"
                header={t("user_management.usage_screen.table.header_tokenCount")}
                sortable={true}
            />
            <Column
                field="cost"
                header={t("user_management.usage_screen.table.header_cost")}
                sortable={false}
                body={(data: TokenUsageInterface) => {
                    return formatCost(calculateTokenCost(data, resolveModelCard(modelCards, data.modelId)), 6);
                }}
            />

            <Column field="createdAt"
                header={t("user_management.usage_screen.table.header_createdAt")}
                body={(data) => {
                    return DateTime.fromISO(data.createdAt).toFormat('dd.MM.yy HH:mm:ss');
                }}
                sortable={true}
            />
        </DataTable>
    </div>
}

export default UserTokenUsageScreen;
